
import { useState } from "react";
import axios from "axios";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Users as UsersIcon, Shield, CheckCircle, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Trash2, Mail } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export default function UsersPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['allUsers'],
    queryFn: async () => {
      // GET /api/staff returns the MongoDB users collection with every staff/admin
      // row, including ap_grade_cohort. Shape it into what this page expects
      // (id, full_name) for compatibility with existing table + mutation code.
      const res = await axios.get(`${BACKEND_URL}/api/staff`, { withCredentials: true });
      return (res.data || []).map((u) => ({
        ...u,
        id: u.user_id,
        full_name: u.name || u.full_name || u.email,
      }));
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, role, userType }) => {
      console.log('Updating user:', { userId, role, userType });
      const res = await axios.patch(
        `${BACKEND_URL}/api/staff/${encodeURIComponent(userId)}`,
        { role, user_type: userType },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['allUsers']);
      queryClient.invalidateQueries(['currentUser']); // Invalidate current user cache
      const displayRole = variables.role === 'admin' ? 'Administrator' : 
                         variables.userType === 'principal' ? 'Principal' :
                         variables.userType === 'ea_staff' ? 'EA Staff' :
                         variables.userType === 'driveline' ? 'Driveline Staff' :
                         variables.userType === 'assistant_principal' ? 'Assistant Principal' :
                         variables.userType === 'coach' ? 'Coach' :
                         variables.userType === 'teacher' ? 'Teacher' : 'Student';
      setMessage({ 
        type: 'success', 
        text: `User role updated to ${displayRole}! The user must log out and log back in for changes to take effect.` 
      });
      setTimeout(() => setMessage(null), 8000);
    },
    onError: (err) => {
      console.error('Update error:', err);
      setMessage({ 
        type: 'error', 
        text: err.message || 'Failed to update user permissions.' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const updateAPCohortMutation = useMutation({
    mutationFn: async ({ userId, cohort }) => {
      const res = await axios.patch(
        `${BACKEND_URL}/api/staff/${encodeURIComponent(userId)}`,
        { ap_grade_cohort: cohort },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['allUsers']);
      setMessage({ 
        type: 'success', 
        text: 'AP grade cohort updated successfully!' 
      });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Failed to update AP cohort.' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const updateTeacherGradeMutation = useMutation({
    mutationFn: async ({ userId, gradeLevel }) => {
      const res = await axios.patch(
        `${BACKEND_URL}/api/staff/${encodeURIComponent(userId)}`,
        { teacher_grade_level: gradeLevel },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['allUsers']);
      setMessage({ 
        type: 'success', 
        text: 'Teacher grade level updated successfully!' 
      });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Failed to update teacher grade level.' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      await axios.delete(
        `${BACKEND_URL}/api/staff/${encodeURIComponent(userId)}`,
        { withCredentials: true },
      );
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['allUsers']);
      setMessage({ 
        type: 'success', 
        text: 'User deleted successfully!' 
      });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Failed to delete user.' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: async ({ userId, email }) => {
      return base44.entities.User.update(userId, { email });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['allUsers']);
      setShowEditDialog(false);
      setEditingUser(null);
      setEditEmail("");
      setMessage({ 
        type: 'success', 
        text: 'Email updated successfully! User will need to log in with the new email.' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Failed to update email.' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const handleDeleteUser = (user) => {
    const userName = getDisplayName(user);
    const confirmMessage = `⚠️ DELETE USER?\n\nUser: ${userName}\nEmail: ${user.email}\n\nThis will permanently delete this user account. They will lose access to the system.\n\nAre you sure you want to delete this user?`;
    
    if (confirm(confirmMessage)) {
      deleteUserMutation.mutate(user.id);
    }
  };

  const handleEditEmail = (user) => {
    setEditingUser(user);
    setEditEmail(user.email || "");
    setShowEditDialog(true);
  };

  const handleSaveEmail = () => {
    if (!editEmail.trim()) {
      setMessage({ type: 'error', text: 'Email cannot be empty' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (!editEmail.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    updateEmailMutation.mutate({ userId: editingUser.id, email: editEmail.trim() });
  };

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="w-4 h-4 ml-1 text-blue-600" /> :
      <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  // Function to get permission sort value
  const getPermissionSortValue = (user) => {
    if (user.role === 'admin') return 1;
    const userType = user.user_type || 'student';
    const sortOrder = {
      'principal': 2,
      'assistant_principal': 3,
      'ea_staff': 4,
      'coach': 5,
      'driveline': 6,
      'teacher': 7,
      'student': 8
    };
    return sortOrder[userType] || 8;
  };

  // Apply sorting
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue, bValue;

    if (sortConfig.key === 'permission') {
      aValue = getPermissionSortValue(a);
      bValue = getPermissionSortValue(b);
    } else if (sortConfig.key === 'full_name') {
      aValue = (a.full_name || '').toLowerCase();
      bValue = (b.full_name || '').toLowerCase();
    } else if (sortConfig.key === 'email') {
      aValue = (a.email || '').toLowerCase();
      bValue = (b.email || '').toLowerCase();
    } else {
      return 0;
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getUserBadge = (user) => {
    if (user.role === 'admin') {
      return { color: "bg-purple-100 text-purple-800", label: "Administrator" };
    }
    
    const userType = user.user_type || 'student';
    const badges = {
      ea_staff: { color: "bg-blue-100 text-blue-800", label: "EA Staff" },
      driveline: { color: "bg-orange-100 text-orange-800", label: "Driveline Staff" },
      principal: { color: "bg-fuchsia-100 text-fuchsia-800", label: "Principal" },
      assistant_principal: { color: "bg-indigo-100 text-indigo-800", label: "Assistant Principal" },
      coach: { color: "bg-teal-100 text-teal-800", label: "Coach" },
      teacher: { color: "bg-green-100 text-green-800", label: "Teacher" },
      student: { color: "bg-slate-100 text-slate-800", label: "Student" }
    };
    return badges[userType] || badges.student;
  };

  const handleRoleChange = (userId, value) => {
    if (value === 'admin') {
      updateUserMutation.mutate({ userId, role: 'admin', userType: null });
    } else {
      // value is ea_staff, driveline, assistant_principal, teacher, or student
      updateUserMutation.mutate({ userId, role: 'user', userType: value });
    }
  };

  const getCurrentValue = (user) => {
    if (user.role === 'admin') return 'admin';
    return user.user_type || 'student';
  };

  const isAP = (user) => {
    return user.role === 'user' && user.user_type === 'assistant_principal';
  };

  const isTeacher = (user) => {
    return user.role === 'user' && user.user_type === 'teacher';
  };

  const getDisplayName = (user) => {
    // For teachers, prioritize teacher_full_name
    if (user.user_type === 'teacher' && user.teacher_full_name) {
      return user.teacher_full_name;
    }
    return user.full_name || user.email;
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-600 mt-1">Manage User Roles and Permissions</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <strong>Permission Structure:</strong> There are 2 role levels (Admin & User) with 5 user types (EA Staff, Driveline, Assistant Principal, Teacher, Student). Admins have full access. APs have admin access + their consequence tracking page. Users have limited access based on type.
          </AlertDescription>
        </Alert>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UsersIcon className="w-5 h-5" />
                All Users ({sortedUsers.length})
              </div>
            </CardTitle>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Actions</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('full_name')}
                    >
                      <div className="flex items-center">
                        Name
                        {getSortIcon('full_name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center">
                        Email
                        {getSortIcon('email')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('permission')}
                    >
                      <div className="flex items-center">
                        Current Permission
                        {getSortIcon('permission')}
                      </div>
                    </TableHead>
                    <TableHead>Change Permission</TableHead>
                    <TableHead>Teacher Grade</TableHead>
                    <TableHead>AP Cohort</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Loading users...
                      </TableCell>
                    </TableRow>
                  ) : sortedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedUsers.map((user) => {
                      const badge = getUserBadge(user);
                      return (
                        <TableRow key={user.id} className="hover:bg-slate-50">
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditEmail(user)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Edit email"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(user)}
                                disabled={deleteUserMutation.isPending}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{getDisplayName(user)}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge className={badge.color}>
                              {badge.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={getCurrentValue(user)}
                              onValueChange={(value) => handleRoleChange(user.id, value)}
                              disabled={updateUserMutation.isPending}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Administrator</span>
                                    <span className="text-xs text-slate-500">Full system access</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="principal">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Principal</span>
                                    <span className="text-xs text-slate-500">AP access, all grades (no cohort filter)</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="assistant_principal">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Assistant Principal</span>
                                    <span className="text-xs text-slate-500">Admin access + consequence tracking</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="ea_staff">
                                  <div className="flex flex-col">
                                    <span className="font-medium">EA Staff</span>
                                    <span className="text-xs text-slate-500">Clerk & EA Teachers</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="coach">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Coach</span>
                                    <span className="text-xs text-slate-500">ASD Attendance only (Present)</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="driveline">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Driveline Staff</span>
                                    <span className="text-xs text-slate-500">Car rider line EA check</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="teacher">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Teacher</span>
                                    <span className="text-xs text-slate-500">View roster & manage excusals</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="student">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Student</span>
                                    <span className="text-xs text-slate-500">View assignments only</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {isTeacher(user) ? (
                              <Select
                                value={user.teacher_grade_level || ''}
                                onValueChange={(value) => 
                                  updateTeacherGradeMutation.mutate({ userId: user.id, gradeLevel: value })
                                }
                                disabled={updateTeacherGradeMutation.isPending}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue placeholder="Set Grade" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="4">Grade 4</SelectItem>
                                  <SelectItem value="5">Grade 5</SelectItem>
                                  <SelectItem value="6">Grade 6</SelectItem>
                                  <SelectItem value="7">Grade 7</SelectItem>
                                  <SelectItem value="8">Grade 8</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {isAP(user) ? (
                              <Select
                                value={user.ap_grade_cohort || ''}
                                onValueChange={(value) => 
                                  updateAPCohortMutation.mutate({ userId: user.id, cohort: value })
                                }
                                disabled={updateAPCohortMutation.isPending}
                              >
                                <SelectTrigger className="w-28">
                                  <SelectValue placeholder="Set Cohort" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="4-5">Grades 4-5</SelectItem>
                                  <SelectItem value="6-8">Grades 6-8</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Permission Levels:</h3>
            <div className="space-y-3 text-sm text-slate-700">
              <div>
                <Badge className="bg-purple-100 text-purple-800 mb-1">Administrator</Badge>
                <p className="text-xs text-slate-600 ml-2">Full access - Upload reports, manage rosters, view all data, manage users (Coordinator & Assistant Principals)</p>
              </div>
              <div>
                <Badge className="bg-indigo-100 text-indigo-800 mb-1">Assistant Principal</Badge>
                <p className="text-xs text-slate-600 ml-2">Admin access + consequence tracking for their grade cohort (4-5 or 6-8). <strong>Remember to set their cohort!</strong></p>
              </div>
              <div>
                <Badge className="bg-blue-100 text-blue-800 mb-1">EA Staff</Badge>
                <p className="text-xs text-slate-600 ml-2">EA/ASD management - Take attendance, view rosters, NO access to reports or student database (Clerk & EA Teachers)</p>
              </div>
              <div>
                <Badge className="bg-orange-100 text-orange-800 mb-1">Driveline Staff</Badge>
                <p className="text-xs text-slate-600 ml-2">Quick EA check - Search by driveline tag to see if student is in EA (Car rider line staff)</p>
              </div>
              <div>
                <Badge className="bg-green-100 text-green-800 mb-1">Teacher</Badge>
                <p className="text-xs text-slate-600 ml-2">Limited access - View their roster, review excusal requests, manually add students to EA (Regular Teachers). <strong>Remember to set their grade level!</strong></p>
              </div>
              <div>
                <Badge className="bg-slate-100 text-slate-800 mb-1">Student</Badge>
                <p className="text-xs text-slate-600 ml-2">View only - See their assignments, submit excusal requests (Students)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Email Dialog */}
        {showEditDialog && editingUser && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User Email</DialogTitle>
                <DialogDescription>
                  Change the email address for {getDisplayName(editingUser)}. The user will need to log in with the new email.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>
                <Alert className="bg-orange-50 border-orange-200">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <AlertDescription className="text-orange-900 text-sm">
                    <strong>Warning:</strong> Changing the email will require the user to log in with the new email address. Their old email will no longer work.
                  </AlertDescription>
                </Alert>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveEmail}
                  disabled={updateEmailMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {updateEmailMutation.isPending ? 'Saving...' : 'Save Email'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
